import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller } from 'react-hook-form';

interface EnterpriseDetailsSectionProps {
  useSameEnterpriseDetails: boolean;
  control: any;
  errors: any;
  inputValue: string;
  setInputValue: (value: string) => void;
  valueOption: any;
  setValueOption: (value: any) => void;
  fetchPlaceDetails: (placeId: string) => void;
  fetchLocations: (query: string) => void;
  setSelectedPlaceDetails: (value: any) => void;
  errorsText?: { business_name?: string };
  options: any[];
  handleToggleCity?: any;
  noPhysicalAddress?: boolean;
  selectedPlaceDetails?: any;
  resetInputValue?: any;
  setResetInputValue?: any;
  isSameEnterPriseDetail?: boolean;
  manualAddressFields?: boolean;
}

const EnterpriseDetailsForm: React.FC<
  Omit<EnterpriseDetailsSectionProps, 'useSameEnterpriseDetails'>
> = ({
  control,
  errors,
  inputValue,
  setInputValue,
  valueOption,
  setValueOption,
  fetchPlaceDetails,
  fetchLocations,
  setSelectedPlaceDetails,
  errorsText,
  options,
  handleToggleCity,
  noPhysicalAddress,
  selectedPlaceDetails,
  resetInputValue,
  setResetInputValue,
  isSameEnterPriseDetail,
  manualAddressFields,
}) => (
  <>
    <FormControl sx={{ marginBottom: '10px' }} fullWidth>
      <Typography>Full Name</Typography>
      <Controller
        name="fullName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            variant="outlined"
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
            fullWidth
            placeholder='Full Name'
          />
        )}
      />
    </FormControl>

    <FormControl sx={{ marginBottom: '20px' }} fullWidth>
      <Typography>Email</Typography>
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            type="email"
            variant="outlined"
            error={!!errors.email}
            helperText={errors.email?.message}
            fullWidth
             placeholder='Email'
          />
        )}
      />
    </FormControl>

    <FormControl sx={{ marginBottom: '10px' }} fullWidth>
      <Autocomplete
        fullWidth
        size="small"
        inputValue={inputValue}
        onChange={(event, value: any) => {
          if (value) {
            fetchPlaceDetails(value.place_id);
          } else {
            setValueOption(null);
            setSelectedPlaceDetails(null);
          }
        }}
        onInputChange={(event, newInputValue) => {
          setResetInputValue(newInputValue);
          setInputValue(newInputValue || '');
          fetchLocations(newInputValue);
        }}
        filterOptions={(x) => x}
        freeSolo
        value={
          valueOption && Object.keys(valueOption).length > 0
            ? valueOption
            : inputValue
              ? { description: inputValue }
              : resetInputValue
                ? { description: resetInputValue }
                : { description: '' }
        }
        options={options}
        getOptionLabel={(option: any) => option.description}
        renderInput={(params) => (
          <TextField {...params} label="Business Name" variant="outlined" />
        )}
      />

      {errorsText?.business_name && (
        <span style={{ color: 'red', margin: '5px 10px', fontSize: '12px' }}>
          {errorsText.business_name}
        </span>
      )}
    </FormControl>

    {!selectedPlaceDetails &&
      !isSameEnterPriseDetail &&
      !manualAddressFields && (
        <FormControlLabel
          sx={{ mb: '3px' }}
          control={
            <Checkbox
              checked={noPhysicalAddress}
              onChange={handleToggleCity}
              inputProps={{ 'aria-label': 'No physical address' }}
            />
          }
          label="My company does not have a physical address"
        />
      )}
  </>
);

const EnterpriseDetailsSection: React.FC<EnterpriseDetailsSectionProps> = ({
  useSameEnterpriseDetails,
  ...formProps
}) => {
  return useSameEnterpriseDetails ? (
    <Accordion
      sx={{
        '&.MuiAccordion-root': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
          padding: '0px',
        },
      }}
      defaultExpanded
    >
      <AccordionSummary
        sx={{
          '&.MuiAccordionSummary-root': {
            padding: '0px !important',
            margin: '0px !important',
          },
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography>Company Details</Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          '&.MuiAccordionDetails-root': {
            padding: '0px',
          },
        }}
      >
        <EnterpriseDetailsForm {...formProps} isSameEnterPriseDetail={true} />
      </AccordionDetails>
    </Accordion>
  ) : (
    <EnterpriseDetailsForm {...formProps} isSameEnterPriseDetail={false} />
  );
};

export default EnterpriseDetailsSection;
